/**
 * Admin Tier Limits Management
 * 
 * View and edit tier limits for each plan (superadmin only).
 * NULL values mean unlimited.
 */
import React, { useState, useEffect, useCallback } from 'react';
import './Admin.css';
import { getAuthToken } from '../../utils/auth';

const CLOUD_API_BASE = process.env.REACT_APP_CLOUD_API_URL || 'http://localhost:8080';

const fetchTierLimits = async () => {
    const token = getAuthToken();
    const response = await fetch(`${CLOUD_API_BASE}/api/admin/tier-limits`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch tier limits');
    return response.json();
};

const updateTierLimit = async (tier, data) => {
    const token = getAuthToken();
    const response = await fetch(`${CLOUD_API_BASE}/api/admin/tier-limits/${tier}`, {
        method: 'PUT',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update tier limit');
    return response.json();
};

// Icons
const InfinityIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.74-8z"/>
    </svg>
);

const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

const XIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);

const TIER_ORDER = ['FREE', 'STARTER', 'PRO', 'BUSINESS', 'ENTERPRISE'];
const TIER_COLORS = {
    FREE: '#6b7280',
    STARTER: '#3b82f6',
    PRO: '#8b5cf6',
    BUSINESS: '#f59e0b',
    ENTERPRISE: '#10b981'
};

export function AdminTierLimits() {
    const [limits, setLimits] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editingTier, setEditingTier] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState(null);

    const loadLimits = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await fetchTierLimits();
            // Sort by our defined order
            data.sort((a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier));
            setLimits(data);
        } catch (err) {
            console.error('Failed to load tier limits:', err);
            setMessage({ type: 'error', text: err.message });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadLimits();
    }, [loadLimits]);

    const handleEdit = (tier) => {
        const current = limits.find(l => l.tier === tier);
        setEditingTier(tier);
        setEditValues({
            max_projects: current.max_projects ?? '',
            max_agent_configs: current.max_agent_configs ?? '',
            agent_designer_access: current.agent_designer_access,
            max_preset_level: current.max_preset_level || 'HIGH',
            priority_queue: current.priority_queue,
            custom_domains: current.custom_domains
        });
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const payload = {
                max_projects: editValues.max_projects === '' ? 0 : parseInt(editValues.max_projects, 10),
                max_agent_configs: editValues.max_agent_configs === '' ? 0 : parseInt(editValues.max_agent_configs, 10),
                agent_designer_access: editValues.agent_designer_access,
                max_preset_level: editValues.max_preset_level || 'HIGH',
                priority_queue: editValues.priority_queue,
                custom_domains: editValues.custom_domains
            };
            await updateTierLimit(editingTier, payload);
            setEditingTier(null);
            setMessage({ type: 'success', text: `${editingTier} tier limits updated!` });
            loadLimits();
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setIsSaving(false);
        }
        setTimeout(() => setMessage(null), 3000);
    };

    return (
        <div className="admin-users">
            <div className="admin-header">
                <h1>Tier Limits</h1>
                <p>Configure plan limits for each tier. Leave empty for unlimited.</p>
            </div>

            {message && (
                <div className={`admin-message ${message.type}`} style={{
                    padding: '10px 16px',
                    marginBottom: '16px',
                    borderRadius: '8px',
                    background: message.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                    color: message.type === 'success' ? '#22c55e' : '#ef4444',
                    border: `1px solid ${message.type === 'success' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`
                }}>
                    {message.text}
                </div>
            )}

            <div className="admin-table-container">
                {isLoading ? (
                    <div className="table-loading">
                        <div className="spinner"></div>
                    </div>
                ) : (
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Tier</th>
                                <th>Max Projects</th>
                                <th>Max Agent Configs</th>
                                <th>Agent Designer</th>
                                <th>Max Preset</th>
                                <th>Priority Queue</th>
                                <th>Custom Domains</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {limits.map(limit => (
                                <tr key={limit.tier}>
                                    <td>
                                        <span className="tier-badge" style={{
                                            background: `${TIER_COLORS[limit.tier]}20`,
                                            color: TIER_COLORS[limit.tier],
                                            border: `1px solid ${TIER_COLORS[limit.tier]}40`
                                        }}>
                                            {limit.tier}
                                        </span>
                                    </td>

                                    {editingTier === limit.tier ? (
                                        <>
                                            <td>
                                                <input
                                                    type="number"
                                                    value={editValues.max_projects}
                                                    onChange={e => setEditValues(v => ({ ...v, max_projects: e.target.value }))}
                                                    placeholder="∞ unlimited"
                                                    min="0"
                                                    style={{ width: '100px' }}
                                                    className="tier-input"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    value={editValues.max_agent_configs}
                                                    onChange={e => setEditValues(v => ({ ...v, max_agent_configs: e.target.value }))}
                                                    placeholder="∞ unlimited"
                                                    min="0"
                                                    style={{ width: '100px' }}
                                                    className="tier-input"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={editValues.agent_designer_access}
                                                    onChange={e => setEditValues(v => ({ ...v, agent_designer_access: e.target.checked }))}
                                                />
                                            </td>
                                            <td>
                                                <select
                                                    className="tier-input"
                                                    value={editValues.max_preset_level || 'HIGH'}
                                                    onChange={e => setEditValues(v => ({ ...v, max_preset_level: e.target.value }))}
                                                >
                                                    <option value="LOW">Low</option>
                                                    <option value="MID">Mid</option>
                                                    <option value="HIGH">High</option>
                                                </select>
                                            </td>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={editValues.priority_queue}
                                                    onChange={e => setEditValues(v => ({ ...v, priority_queue: e.target.checked }))}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={editValues.custom_domains}
                                                    onChange={e => setEditValues(v => ({ ...v, custom_domains: e.target.checked }))}
                                                />
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                    <button
                                                        className="action-btn"
                                                        onClick={handleSave}
                                                        disabled={isSaving}
                                                        style={{ background: '#22c55e20', color: '#22c55e' }}
                                                    >
                                                        {isSaving ? '...' : 'Save'}
                                                    </button>
                                                    <button
                                                        className="action-btn"
                                                        onClick={() => setEditingTier(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td>
                                                <span className={limit.max_projects === null ? 'unlimited' : ''}>
                                                    {limit.max_projects === null ? <><InfinityIcon /> Unlimited</> : limit.max_projects}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={limit.max_agent_configs === null ? 'unlimited' : ''}>
                                                    {limit.max_agent_configs === null ? <><InfinityIcon /> Unlimited</> : limit.max_agent_configs}
                                                </span>
                                            </td>
                                            <td>{limit.agent_designer_access ? <CheckIcon /> : <XIcon />}</td>
                                            <td>{limit.max_preset_level || 'HIGH'}</td>
                                            <td>{limit.priority_queue ? <CheckIcon /> : <XIcon />}</td>
                                            <td>{limit.custom_domains ? <CheckIcon /> : <XIcon />}</td>
                                            <td>
                                                <button
                                                    className="action-btn"
                                                    onClick={() => handleEdit(limit.tier)}
                                                >
                                                    Edit
                                                </button>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div style={{ marginTop: '16px', color: 'var(--text-tertiary)', fontSize: '12px' }}>
                <p><strong>Tip:</strong> Leave number fields empty to set unlimited. Changes take effect immediately for all users on that tier.</p>
            </div>
        </div>
    );
}

export default AdminTierLimits;

